import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import ImageUpload from '../components/ImageUpload';

const ArtistDashboard = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const { user, token, isArtist } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'painting',
    price: '',
    images: [''],
    medium: '',
    year: new Date().getFullYear(),
    dimensions: {
      width: '',
      height: '',
      depth: '',
      unit: 'cm'
    },
    tags: '',
    featured: false,
    inStock: true
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!isArtist || !user) {
      navigate('/');
      return;
    }
    fetchMyArtworks();
  }, [isArtist, user]);

  const fetchMyArtworks = async () => {
    if (!user || !user._id) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/artworks/artist/${user._id}`
      );
      setArtworks(response.data);
    } catch (error) {
      console.error('Error fetching artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('dimensions.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        dimensions: { ...formData.dimensions, [field]: value }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ''] });
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const artworkData = {
        ...formData,
        images: formData.images.filter(img => img.trim() !== ''),
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        price: parseFloat(formData.price),
        year: parseInt(formData.year),
        dimensions: {
          ...formData.dimensions,
          width: parseFloat(formData.dimensions.width) || undefined,
          height: parseFloat(formData.dimensions.height) || undefined,
          depth: parseFloat(formData.dimensions.depth) || undefined
        }
      };

      if (editingArtwork) {
        await axios.put(
          `${API_URL}/api/artworks/${editingArtwork._id}`,
          artworkData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showToast('Artwork updated successfully!', 'success');
      } else {
        await axios.post(
          `${API_URL}/api/artworks`,
          artworkData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showToast('Artwork uploaded successfully!', 'success');
      }

      setShowUploadModal(false);
      setEditingArtwork(null);
      resetForm();
      fetchMyArtworks();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save artwork', 'error');
    }
  };

  const handleEdit = (artwork) => {
    setEditingArtwork(artwork);
    setFormData({
      title: artwork.title,
      description: artwork.description,
      category: artwork.category,
      price: artwork.price,
      images: artwork.images.length > 0 ? artwork.images : [''],
      medium: artwork.medium || '',
      year: artwork.year || new Date().getFullYear(),
      dimensions: artwork.dimensions || { width: '', height: '', depth: '', unit: 'cm' },
      tags: artwork.tags?.join(', ') || '',
      featured: artwork.featured || false,
      inStock: artwork.inStock !== undefined ? artwork.inStock : true
    });
    setShowUploadModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) return;

    try {
      await axios.delete(`${API_URL}/api/artworks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Artwork deleted successfully!', 'success');
      fetchMyArtworks();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete artwork', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'painting',
      price: '',
      images: [''],
      medium: '',
      year: new Date().getFullYear(),
      dimensions: { width: '', height: '', depth: '', unit: 'cm' },
      tags: '',
      featured: false,
      inStock: true
    });
  };

  const stats = {
    total: artworks.length,
    available: artworks.filter(a => a.inStock).length,
    sold: artworks.filter(a => !a.inStock).length,
    totalViews: artworks.reduce((sum, a) => sum + (a.views || 0), 0)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-2 african-accent inline-block">
              My Artworks
            </h1>
            <p className="text-gray-600">Manage your art collection</p>
          </div>
          <button
            onClick={() => {
              setEditingArtwork(null);
              resetForm();
              setShowUploadModal(true);
            }}
            className="btn-primary"
          >
            + Upload Artwork
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Artworks', value: stats.total, icon: '🎨', color: 'from-blue-500 to-blue-600' },
            { label: 'Available', value: stats.available, icon: '✅', color: 'from-green-500 to-green-600' },
            { label: 'Sold', value: stats.sold, icon: '💰', color: 'from-orange-500 to-red-600' },
            { label: 'Total Views', value: stats.totalViews, icon: '👁️', color: 'from-purple-500 to-purple-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-african transition-shadow duration-300">
              <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center text-2xl mb-4`}>
                {stat.icon}
              </div>
              <p className="text-gray-600 text-sm mb-1">{stat.label}</p>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Artworks Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 mx-auto"></div>
          </div>
        ) : artworks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {artworks.map((artwork) => (
              <div key={artwork._id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-african transition-shadow duration-300">
                <div className="relative">
                  <img
                    src={artwork.images?.[0] || 'https://via.placeholder.com/400x300/fb923c/ffffff?text=No+Image'}
                    alt={artwork.title}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300/fb923c/ffffff?text=Image+Error';
                    }}
                    className="w-full h-64 object-cover"
                  />
                  {!artwork.inStock && (
                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      Sold
                    </div>
                  )}
                  {artwork.featured && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      ⭐ Featured
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-2 truncate">
                    {artwork.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {artwork.description}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                      ${artwork.price?.toLocaleString()}
                    </span>
                    <span className="text-sm text-gray-500">
                      👁️ {artwork.views || 0} views
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(artwork)}
                      className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(artwork._id)}
                      className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
            <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-orange-200 to-red-200 rounded-full flex items-center justify-center">
              <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
              No Artworks Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start uploading your amazing artworks to showcase to the world!
            </p>
            <button onClick={() => setShowUploadModal(true)} className="btn-primary">
              Upload Your First Artwork
            </button>
          </div>
        )}
      </div>

      {/* Upload/Edit Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-african-lg w-full max-w-3xl my-8">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-display font-bold text-gray-900">
                  {editingArtwork ? 'Edit Artwork' : 'Upload New Artwork'}
                </h2>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setEditingArtwork(null);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                    placeholder="Enter artwork title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                    placeholder="Describe your artwork"
                  />
                </div>

                {/* Category and Price */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      required
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 outline-none cursor-pointer"
                    >
                      <option value="painting">Painting</option>
                      <option value="digital-art">Digital Art</option>
                      <option value="sculpture">Sculpture</option>
                      <option value="photography">Photography</option>
                      <option value="mixed-media">Mixed Media</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Price ($) *
                    </label>
                    <input
                      type="number"
                      name="price"
                      required
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Images */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Artwork Images *
                  </label>
                  {formData.images.map((image, index) => (
                    <div key={index} className="mb-6 p-4 bg-orange-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-700">
                          Image {index + 1}
                        </span>
                        {formData.images.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeImageField(index)}
                            className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors duration-200"
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      <ImageUpload
                        existingUrl={image}
                        onImageUploaded={(url) => handleImageChange(index, url)}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addImageField}
                    className="w-full py-3 border-2 border-dashed border-orange-300 text-orange-600 hover:border-orange-500 hover:bg-orange-50 rounded-xl font-semibold transition-all duration-200"
                  >
                    + Add Another Image
                  </button>
                </div>

                {/* Medium and Year */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Medium
                    </label>
                    <input
                      type="text"
                      name="medium"
                      value={formData.medium}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                      placeholder="e.g., Oil on canvas"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Dimensions
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    <input
                      type="number"
                      name="dimensions.width"
                      value={formData.dimensions.width}
                      onChange={handleInputChange}
                      placeholder="Width"
                      className="px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 outline-none"
                    />
                    <input
                      type="number"
                      name="dimensions.height"
                      value={formData.dimensions.height}
                      onChange={handleInputChange}
                      placeholder="Height"
                      className="px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 outline-none"
                    />
                    <input
                      type="number"
                      name="dimensions.depth"
                      value={formData.dimensions.depth}
                      onChange={handleInputChange}
                      placeholder="Depth"
                      className="px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 outline-none"
                    />
                    <select
                      name="dimensions.unit"
                      value={formData.dimensions.unit}
                      onChange={handleInputChange}
                      className="px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 outline-none cursor-pointer"
                    >
                      <option value="cm">cm</option>
                      <option value="inch">inch</option>
                      <option value="mm">mm</option>
                    </select>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                    placeholder="abstract, modern, colorful"
                  />
                </div>

                {/* Checkboxes */}
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm font-semibold text-gray-700">
                      Featured Artwork
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={formData.inStock}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm font-semibold text-gray-700">
                      Available for Sale
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button type="submit" className="w-full btn-primary text-lg py-4">
                  {editingArtwork ? 'Update Artwork' : 'Upload Artwork'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistDashboard;